import React, { createContext, useContext, useEffect, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { useMotionPreference } from '../hooks/useMotionPreference';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import NebulaField from './NebulaField';
import '../arunkube-space.css';

const SpaceContext = createContext(null);
export const useSpaceExperience = () => useContext(SpaceContext);

function stored(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

export function SpaceMotionControl() {
  const space = useSpaceExperience();
  if (!space) return null;
  return <button className="space-motion-button" type="button" aria-label={space.paused ? 'Play cosmic motion' : 'Pause cosmic motion'} aria-pressed={space.paused} disabled={space.reduced} title={space.reduced ? 'Motion follows your reduced-motion preference' : space.paused ? 'Play cosmic motion' : 'Pause cosmic motion'} onClick={() => space.setPaused(!space.paused)}>
    {space.paused ? <Play size={16} /> : <Pause size={16} />}
  </button>;
}

export default function SpaceExperience({ children, className = '' }) {
  const reduced = useMotionPreference();
  const [mode, setMode] = useState(() => stored('arunkube-appearance', 'dark') === 'light' ? 'light' : 'dark');
  const [userPaused, setPaused] = useState(() => stored('arunkube-motion', 'on') === 'off');
  const paused = userPaused || reduced;
  useSmoothScroll(paused);
  useEffect(() => {
    try { localStorage.setItem('arunkube-appearance', mode); localStorage.setItem('arunkube-motion', userPaused ? 'off' : 'on'); } catch { /* Preferences remain usable without storage. */ }
  }, [mode, userPaused]);
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousColor = meta?.getAttribute('content');
    const icon = document.querySelector('link[rel="icon"]');
    const previousIcon = icon?.getAttribute('href');
    document.title = window.location.pathname === '/blogs' ? 'Arun Bharathwaj · Engineering journal' : 'Arun Bharathwaj · Platform Modernization Architect';
    icon?.setAttribute('href', '/arunkube-symbol.svg');
    meta?.setAttribute('content', mode === 'dark' ? '#070b18' : '#eef3ff');
    return () => {
      document.title = previousTitle;
      if (previousColor) meta?.setAttribute('content', previousColor);
      if (previousIcon) icon?.setAttribute('href', previousIcon);
    };
  }, [mode]);
  return <SpaceContext.Provider value={{ mode, setMode, paused, setPaused, reduced }}>
    <MotionConfig reducedMotion={paused ? 'always' : 'user'}>
      <div className={`portfolio-trial portfolio-immersive arunkube-space ${paused ? 'space-motion-paused' : ''} ${className}`} data-space-mode={mode}>
        <NebulaField paused={paused} mode={mode} />
        {children}
      </div>
    </MotionConfig>
  </SpaceContext.Provider>;
}
